import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import {
  useAssignableRoles,
  type AssignableRole,
} from '@/hooks/submissions/useAssignableRoles';

interface ModifyRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Resource base path for fetching assignable roles, e.g. /studies/123 or /submissions/456 */
  resourcePath: string;
  /**
   * Roles to offer, when the caller already knows them (the row's own
   * assignable_roles). Skips the fetch from `resourcePath`, which only knows
   * the page's level — wrong for a site/submission row on a Study or Site tab.
   */
  roles?: AssignableRole[];
  userName: string;
  currentRoleKey: string;
  currentRoleName: string;
  onConfirm: (newRoleKey: string) => Promise<void>;
  isLoading?: boolean;
}

interface ModalContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourcePath: string;
  roles?: AssignableRole[];
  userName: string;
  currentRoleKey: string;
  currentRoleName: string;
  onConfirm: (newRoleKey: string) => Promise<void>;
  isLoading: boolean;
}

// Inner component is remounted when the modal opens/closes via the `key` prop,
// which resets all state without needing a useEffect + setState.
const ModalContent: React.FC<ModalContentProps> = ({
  open,
  onOpenChange,
  resourcePath,
  roles: providedRoles,
  userName,
  currentRoleKey,
  currentRoleName,
  onConfirm,
  isLoading,
}) => {
  const [selectedRole, setSelectedRole] = useState<string>(currentRoleKey);
  const [error, setError] = useState<string | null>(null);
  const {
    roles: fetchedRoles,
    isLoading: isFetchingRoles,
    error: rolesError,
  } = useAssignableRoles(resourcePath, { enabled: !providedRoles });
  const roles = providedRoles ?? fetchedRoles;
  const isLoadingRoles = !providedRoles && isFetchingRoles;

  const handleConfirm = async () => {
    if (selectedRole === currentRoleKey) {
      onOpenChange(false);
      return;
    }

    setError(null);
    try {
      await onConfirm(selectedRole);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleCancel = () => {
    setSelectedRole(currentRoleKey);
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Modify User Role</DialogTitle>
          <DialogDescription>
            Change the role for {userName}. Current role: {currentRoleName}
          </DialogDescription>
        </DialogHeader>

        {(error || rolesError) && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || rolesError}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="role">New Role</Label>
            <Select
              value={selectedRole}
              onValueChange={setSelectedRole}
              disabled={isLoadingRoles}
            >
              <SelectTrigger id="role">
                <SelectValue
                  placeholder={
                    isLoadingRoles ? 'Loading roles...' : 'Select a role'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {roles.map(role => (
                  <SelectItem key={role.key} value={role.key}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={
              isLoading || isLoadingRoles || selectedRole === currentRoleKey
            }
          >
            {isLoading ? 'Updating...' : 'Update Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ModifyRoleModal: React.FC<ModifyRoleModalProps> = ({
  open,
  onOpenChange,
  resourcePath,
  roles,
  userName,
  currentRoleKey,
  currentRoleName,
  onConfirm,
  isLoading = false,
}) => (
  // key resets ModalContent state when the modal opens with a new role,
  // avoiding the need for a useEffect + setState to sync selectedRole.
  <ModalContent
    key={`${open ? 'open' : 'closed'}-${currentRoleKey}`}
    open={open}
    onOpenChange={onOpenChange}
    resourcePath={resourcePath}
    roles={roles}
    userName={userName}
    currentRoleKey={currentRoleKey}
    currentRoleName={currentRoleName}
    onConfirm={onConfirm}
    isLoading={isLoading}
  />
);

export default ModifyRoleModal;
